import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Tabs,
  Space,
  Spin,
  message,
  Image,
  Typography,
  Divider,
  Tag,
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CaptionEditorProps {
  imageId: number | null;
  visible: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface CaptionData {
  image_id: number;
  s3_url: string;
  thumbnail_uri: string;
  bucket_name: string;
  full_data: any;
  ten_word_summary?: string;
  brief_summary?: string;
  regular_summary?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const CaptionEditor: React.FC<CaptionEditorProps> = ({
  imageId,
  visible,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [captionData, setCaptionData] = useState<CaptionData | null>(null);
  const [editedJson, setEditedJson] = useState<string>('');
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('fields');

  useEffect(() => {
    if (visible && imageId) {
      fetchCaptionData();
    }
  }, [visible, imageId]);

  const fetchCaptionData = async () => {
    if (!imageId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/captions/${imageId}`);
      setCaptionData(response.data);
      setEditedJson(JSON.stringify(response.data.full_data, null, 2));

      // Populate form with parsed values
      const fullData = response.data.full_data || {};
      const metadata = fullData.image_metadata || {};
      const peopleAnalysis = fullData.people_analysis || {};
      const person1 = peopleAnalysis.person_1 || {};
      const summaries = fullData.structured_summaries || fullData.summaries || {};

      form.setFieldsValue({
        // Watermark
        has_watermark: metadata.watermark_info?.has_watermark,
        watermark_text: metadata.watermark_info?.watermark_text,
        watermark_location: metadata.watermark_info?.watermark_location,

        // Quality flags
        has_face_filter: metadata.image_quality_flags?.has_face_filter,
        is_collage: metadata.image_quality_flags?.is_collage,
        has_blurred_parts: metadata.image_quality_flags?.has_blurred_parts,
        poor_quality: metadata.image_quality_flags?.poor_quality,
        heavily_edited: metadata.image_quality_flags?.heavily_edited,

        // Selfie classification
        is_selfie: metadata.selfie_classification?.is_selfie,
        photo_type: metadata.selfie_classification?.photo_type,
        framing_focus: metadata.selfie_classification?.framing_focus,
        camera_distance: metadata.selfie_classification?.camera_distance,
        camera_angle: metadata.selfie_classification?.camera_angle,

        // Person 1
        p1_estimated_age: person1.human_characteristics?.estimated_age,
        p1_body_type: person1.human_characteristics?.body_type,
        p1_apparent_race: person1.human_characteristics?.apparent_race,
        p1_gender_identity: person1.human_characteristics?.gender_identity,
        p1_pose_category: person1.pose_analysis?.pose_category,
        p1_primary_pose: person1.pose_analysis?.primary_pose,
        p1_overall_expression: person1.facial_expression_analysis?.overall_expression,

        // Summaries
        ten_word_summary: summaries.ten_word_summary || summaries['10_word_summary'],
        brief_summary: summaries.brief_summary || summaries.short_summary,
        regular_summary: summaries.regular_summary || summaries.medium_summary,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        message.warning('No caption data found for this image');
        setCaptionData(null);
      } else {
        console.error('Failed to fetch caption:', error);
        message.error('Failed to load caption data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!imageId || !captionData) return;

    setSaving(true);
    try {
      let jsonToSave: any;

      if (activeTab === 'json') {
        // Save raw JSON
        try {
          jsonToSave = JSON.parse(editedJson);
        } catch (e) {
          message.error('Invalid JSON format');
          setSaving(false);
          return;
        }
      } else {
        // Build JSON from form values
        const values = form.getFieldsValue();
        jsonToSave = captionData.full_data || {};

        // Update watermark info
        if (!jsonToSave.image_metadata) jsonToSave.image_metadata = {};
        if (!jsonToSave.image_metadata.watermark_info) jsonToSave.image_metadata.watermark_info = {};
        jsonToSave.image_metadata.watermark_info.has_watermark = values.has_watermark;
        jsonToSave.image_metadata.watermark_info.watermark_text = values.watermark_text;
        jsonToSave.image_metadata.watermark_info.watermark_location = values.watermark_location;

        // Update quality flags
        if (!jsonToSave.image_metadata.image_quality_flags) jsonToSave.image_metadata.image_quality_flags = {};
        jsonToSave.image_metadata.image_quality_flags.has_face_filter = values.has_face_filter;
        jsonToSave.image_metadata.image_quality_flags.is_collage = values.is_collage;
        jsonToSave.image_metadata.image_quality_flags.has_blurred_parts = values.has_blurred_parts;
        jsonToSave.image_metadata.image_quality_flags.poor_quality = values.poor_quality;
        jsonToSave.image_metadata.image_quality_flags.heavily_edited = values.heavily_edited;

        // Update selfie classification
        if (!jsonToSave.image_metadata.selfie_classification) jsonToSave.image_metadata.selfie_classification = {};
        jsonToSave.image_metadata.selfie_classification.is_selfie = values.is_selfie;
        jsonToSave.image_metadata.selfie_classification.photo_type = values.photo_type;
        jsonToSave.image_metadata.selfie_classification.framing_focus = values.framing_focus;
        jsonToSave.image_metadata.selfie_classification.camera_distance = values.camera_distance;
        jsonToSave.image_metadata.selfie_classification.camera_angle = values.camera_angle;

        // Update person 1
        if (!jsonToSave.people_analysis) jsonToSave.people_analysis = {};
        if (!jsonToSave.people_analysis.person_1) jsonToSave.people_analysis.person_1 = {};
        if (!jsonToSave.people_analysis.person_1.human_characteristics) jsonToSave.people_analysis.person_1.human_characteristics = {};
        jsonToSave.people_analysis.person_1.human_characteristics.estimated_age = values.p1_estimated_age;
        jsonToSave.people_analysis.person_1.human_characteristics.body_type = values.p1_body_type;
        jsonToSave.people_analysis.person_1.human_characteristics.apparent_race = values.p1_apparent_race;
        jsonToSave.people_analysis.person_1.human_characteristics.gender_identity = values.p1_gender_identity;

        if (!jsonToSave.people_analysis.person_1.pose_analysis) jsonToSave.people_analysis.person_1.pose_analysis = {};
        jsonToSave.people_analysis.person_1.pose_analysis.pose_category = values.p1_pose_category;
        jsonToSave.people_analysis.person_1.pose_analysis.primary_pose = values.p1_primary_pose;

        if (!jsonToSave.people_analysis.person_1.facial_expression_analysis) jsonToSave.people_analysis.person_1.facial_expression_analysis = {};
        jsonToSave.people_analysis.person_1.facial_expression_analysis.overall_expression = values.p1_overall_expression;

        // Update summaries
        if (!jsonToSave.structured_summaries) jsonToSave.structured_summaries = {};
        jsonToSave.structured_summaries.ten_word_summary = values.ten_word_summary;
        jsonToSave.structured_summaries.brief_summary = values.brief_summary;
        jsonToSave.structured_summaries.regular_summary = values.regular_summary;
      }

      await axios.put(`${API_URL}/admin/captions/${imageId}`, {
        full_data: jsonToSave,
      });

      message.success('Caption saved successfully');
      onSave();
    } catch (error) {
      console.error('Failed to save caption:', error);
      message.error('Failed to save caption');
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = () => {
    if (!captionData) return '';
    return captionData.s3_url;
  };

  return (
    <Drawer
      title={
        <Space>
          <span>Edit Caption</span>
          {imageId && <Tag color="blue">ID: {imageId}</Tag>}
        </Space>
      }
      placement="right"
      width={800}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button onClick={onClose} icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            icon={<SaveOutlined />}
            disabled={!captionData}
          >
            Save
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin size="large" />
        </div>
      ) : !captionData ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">No caption data available for this image.</Text>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Left: Image Preview */}
          <div style={{ width: '300px', flexShrink: 0 }}>
            <Image
              src={getImageUrl()}
              alt="Preview"
              style={{ width: '100%', borderRadius: '8px' }}
              fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><rect fill='%23f0f0f0' width='300' height='300'/></svg>"
            />
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {captionData.bucket_name}
              </Text>
            </div>
          </div>

          {/* Right: Edit Form */}
          <div style={{ flex: 1 }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'fields',
                  label: 'Fields',
                  children: (
                    <Form
                      form={form}
                      layout="vertical"
                      size="small"
                      style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
                    >
                      {/* Summaries */}
                      <Title level={5}>Summaries</Title>
                      <Form.Item name="ten_word_summary" label="10-Word Summary">
                        <Input />
                      </Form.Item>
                      <Form.Item name="brief_summary" label="Brief Summary">
                        <TextArea rows={2} />
                      </Form.Item>
                      <Form.Item name="regular_summary" label="Regular Summary">
                        <TextArea rows={3} />
                      </Form.Item>

                      <Divider />

                      {/* Quality Flags */}
                      <Title level={5}>Quality Flags</Title>
                      <Space wrap>
                        <Form.Item name="has_watermark" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Watermark" unCheckedChildren="No Watermark" />
                        </Form.Item>
                        <Form.Item name="poor_quality" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Poor Quality" unCheckedChildren="Good Quality" />
                        </Form.Item>
                        <Form.Item name="heavily_edited" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Edited" unCheckedChildren="Not Edited" />
                        </Form.Item>
                        <Form.Item name="is_collage" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Collage" unCheckedChildren="Not Collage" />
                        </Form.Item>
                        <Form.Item name="has_face_filter" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Face Filter" unCheckedChildren="No Filter" />
                        </Form.Item>
                      </Space>

                      <Divider />

                      {/* Photo Classification */}
                      <Title level={5}>Photo Classification</Title>
                      <Space wrap style={{ width: '100%' }}>
                        <Form.Item name="is_selfie" valuePropName="checked" style={{ marginBottom: 8 }}>
                          <Switch checkedChildren="Selfie" unCheckedChildren="Not Selfie" />
                        </Form.Item>
                        <Form.Item name="photo_type" label="Photo Type" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="selfie">Selfie</Select.Option>
                            <Select.Option value="mirror_selfie">Mirror Selfie</Select.Option>
                            <Select.Option value="professional_photo">Professional</Select.Option>
                            <Select.Option value="candid">Candid</Select.Option>
                            <Select.Option value="posed">Posed</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="framing_focus" label="Framing" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="face">Face</Select.Option>
                            <Select.Option value="upper_body">Upper Body</Select.Option>
                            <Select.Option value="full_body">Full Body</Select.Option>
                            <Select.Option value="torso">Torso</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="camera_distance" label="Distance" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="extreme_closeup">Extreme Closeup</Select.Option>
                            <Select.Option value="closeup">Closeup</Select.Option>
                            <Select.Option value="close">Close</Select.Option>
                            <Select.Option value="medium">Medium</Select.Option>
                            <Select.Option value="far">Far</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="camera_angle" label="Angle" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="low_angle">Low Angle</Select.Option>
                            <Select.Option value="eye_level">Eye Level</Select.Option>
                            <Select.Option value="high_angle">High Angle</Select.Option>
                            <Select.Option value="overhead">Overhead</Select.Option>
                          </Select>
                        </Form.Item>
                      </Space>

                      <Divider />

                      {/* Person 1 */}
                      <Title level={5}>Person 1</Title>
                      <Space wrap style={{ width: '100%' }}>
                        <Form.Item name="p1_estimated_age" label="Age" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="teen_18_19">Teen (18-19)</Select.Option>
                            <Select.Option value="young_adult_20_29">Young Adult (20-29)</Select.Option>
                            <Select.Option value="adult_30_39">Adult (30-39)</Select.Option>
                            <Select.Option value="mature_40_49">Mature (40-49)</Select.Option>
                            <Select.Option value="older_50_plus">Older (50+)</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_body_type" label="Body Type" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="petite">Petite</Select.Option>
                            <Select.Option value="slim">Slim</Select.Option>
                            <Select.Option value="athletic">Athletic</Select.Option>
                            <Select.Option value="average">Average</Select.Option>
                            <Select.Option value="curvy">Curvy</Select.Option>
                            <Select.Option value="voluptuous">Voluptuous</Select.Option>
                            <Select.Option value="plus_size">Plus Size</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_apparent_race" label="Race" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="caucasian">Caucasian</Select.Option>
                            <Select.Option value="african_american">African American</Select.Option>
                            <Select.Option value="hispanic_latina">Hispanic/Latina</Select.Option>
                            <Select.Option value="asian">Asian</Select.Option>
                            <Select.Option value="middle_eastern">Middle Eastern</Select.Option>
                            <Select.Option value="indian">Indian</Select.Option>
                            <Select.Option value="mixed">Mixed</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_gender_identity" label="Gender" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="cisgender_female">Female</Select.Option>
                            <Select.Option value="cisgender_male">Male</Select.Option>
                            <Select.Option value="transgender_woman">Trans Woman</Select.Option>
                            <Select.Option value="transgender_man">Trans Man</Select.Option>
                            <Select.Option value="non_binary">Non-Binary</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_pose_category" label="Pose Category" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="casual">Casual</Select.Option>
                            <Select.Option value="explicit">Explicit</Select.Option>
                            <Select.Option value="artistic">Artistic</Select.Option>
                            <Select.Option value="professional">Professional</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_primary_pose" label="Primary Pose" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="standing">Standing</Select.Option>
                            <Select.Option value="sitting">Sitting</Select.Option>
                            <Select.Option value="lying_down">Lying Down</Select.Option>
                            <Select.Option value="kneeling">Kneeling</Select.Option>
                            <Select.Option value="all_fours">All Fours</Select.Option>
                            <Select.Option value="bent_over">Bent Over</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="p1_overall_expression" label="Expression" style={{ width: 150, marginBottom: 8 }}>
                          <Select allowClear>
                            <Select.Option value="neutral">Neutral</Select.Option>
                            <Select.Option value="happy">Happy</Select.Option>
                            <Select.Option value="seductive">Seductive</Select.Option>
                            <Select.Option value="playful">Playful</Select.Option>
                            <Select.Option value="serious">Serious</Select.Option>
                            <Select.Option value="surprised">Surprised</Select.Option>
                          </Select>
                        </Form.Item>
                      </Space>
                    </Form>
                  ),
                },
                {
                  key: 'json',
                  label: 'Raw JSON',
                  children: (
                    <TextArea
                      value={editedJson}
                      onChange={(e) => setEditedJson(e.target.value)}
                      rows={25}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                  ),
                },
              ]}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default CaptionEditor;
